import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Loader2, Globe, Download, Search, CheckCircle, XCircle, Edit2, Save } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WebScraper = () => {
  const [hasAccess, setHasAccess] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scraping, setScraping] = useState(false);
  const [creating, setCreating] = useState(false);
  
  // Scraper form state
  const [url, setUrl] = useState('');
  const [scrapeMode, setScrapeMode] = useState('auto');
  const [cssSelector, setCssSelector] = useState('');
  const [maxResults, setMaxResults] = useState(100);
  
  // Results state
  const [scrapedData, setScrapedData] = useState(null);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [editingContact, setEditingContact] = useState(null);
  
  // Group creation state
  const [groupName, setGroupName] = useState('');
  const [groupDescription, setGroupDescription] = useState('');
  const [defaultLocation, setDefaultLocation] = useState('');
  
  // Available modes
  const [modes, setModes] = useState([]);

  useEffect(() => {
    checkAccess();
    loadModes();
  }, []);

  const checkAccess = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/scraper/access-check`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHasAccess(true);
    } catch (error) {
      setHasAccess(false);
    }
  };

  const loadModes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/scraper/modes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setModes(response.data.modes);
    } catch (error) {
      console.error('Failed to load modes:', error);
    }
  };

  const handleScrape = async () => {
    if (!url) {
      alert('Please enter a URL to scrape');
      return;
    }

    setScraping(true);
    setScrapedData(null);
    setSelectedContacts([]);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/scraper/scrape`,
        {
          url,
          scrape_mode: scrapeMode,
          css_selector: cssSelector || null,
          max_results: maxResults
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setScrapedData(response.data);
      setSelectedContacts(response.data.contacts.map((_, i) => i)); // Select all by default
      
      // Auto-fill group name from URL
      const urlObj = new URL(url);
      setGroupName(`Scraped from ${urlObj.hostname}`);
    } catch (error) {
      alert(`Scraping failed: ${error.response?.data?.detail || error.message}`);
    } finally {
      setScraping(false);
    }
  };

  const toggleContactSelection = (index) => {
    setSelectedContacts(prev => {
      if (prev.includes(index)) {
        return prev.filter(i => i !== index);
      } else {
        return [...prev, index];
      }
    });
  };

  const handleEditContact = (index) => {
    setEditingContact({
      index,
      ...scrapedData.contacts[index]
    });
  };

  const handleSaveEdit = () => {
    if (!editingContact) return;

    const updatedContacts = [...scrapedData.contacts];
    updatedContacts[editingContact.index] = {
      name: editingContact.name || '',
      businessName: editingContact.businessName || editingContact.company || '',
      title: editingContact.title || '',
      email: editingContact.email || '',
      phone: editingContact.phone || '',
      website: editingContact.website || '',
      address: editingContact.address || '',
      socialMedia: editingContact.socialMedia || []
    };

    setScrapedData({
      ...scrapedData,
      contacts: updatedContacts
    });

    setEditingContact(null);
  };

  const handleCreateGroup = async () => {
    if (!groupName.trim()) {
      alert('Please enter a group name');
      return;
    }

    if (selectedContacts.length === 0) {
      alert('Please select at least one contact');
      return;
    }

    setCreating(true);

    try {
      const token = localStorage.getItem('token');
      const selectedContactsData = selectedContacts.map(i => scrapedData.contacts[i]);

      const response = await axios.post(
        `${API_URL}/api/scraper/create-group`,
        {
          group_name: groupName,
          group_description: groupDescription || `Contact group created from web scraping`,
          contacts: selectedContactsData,
          default_location: defaultLocation
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      alert(`✅ ${response.data.message}\n\nGroup ID: ${response.data.group_id}\nContacts: ${response.data.contact_count}`);
      
      // Reset form
      setScrapedData(null);
      setSelectedContacts([]);
      setGroupName('');
      setGroupDescription('');
      setUrl('');
    } catch (error) {
      alert(`Failed to create group: ${error.response?.data?.detail || error.message}`);
    } finally {
      setCreating(false);
    }
  };

  if (hasAccess === null) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (hasAccess === false) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <Card className="max-w-md">
          <CardContent className="pt-6">
            <div className="text-center">
              <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold mb-2">Access Denied</h2>
              <p className="text-gray-600">
                This Web Scraper tool is not available for your account.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Globe className="w-8 h-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">Universal Web Scraper</h1>
          </div>
          <p className="text-gray-600">
            Extract contact data from any website and create contact groups automatically
          </p>
        </div>

        {/* Scraper Form */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Scrape Website</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Website URL
              </label>
              <Input
                type="url"
                placeholder="https://example.com/members"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                className="w-full"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Scrape Mode
                </label>
                <select
                  value={scrapeMode}
                  onChange={(e) => setScrapeMode(e.target.value)}
                  className="w-full p-2 border rounded-lg"
                >
                  {modes.map(mode => (
                    <option key={mode.value} value={mode.value}>
                      {mode.label}
                    </option>
                  ))}
                </select>
                {modes.find(m => m.value === scrapeMode) && (
                  <p className="text-xs text-gray-500 mt-1">
                    {modes.find(m => m.value === scrapeMode).description}
                  </p>
                )}
              </div>

              {scrapeMode === 'custom' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    CSS Selector
                  </label>
                  <Input
                    type="text"
                    placeholder=".member-card"
                    value={cssSelector}
                    onChange={(e) => setCssSelector(e.target.value)}
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Max Results
                </label>
                <Input
                  type="number"
                  min="1"
                  max="500"
                  value={maxResults}
                  onChange={(e) => setMaxResults(parseInt(e.target.value))}
                />
              </div>
            </div>

            <Button
              onClick={handleScrape}
              disabled={scraping || !url}
              className="w-full"
            >
              {scraping ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Scraping...
                </>
              ) : (
                <>
                  <Search className="w-4 h-4 mr-2" />
                  Scrape Website
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Scraped Results */}
        {scrapedData && (
          <>
            <Card className="mb-8">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Scraped Data</CardTitle>
                    <p className="text-sm text-gray-600 mt-1">
                      Found {scrapedData.total_contacts} contacts • {selectedContacts.length} selected
                    </p>
                  </div>
                  <Badge variant="secondary">{scrapedData.scrape_mode} mode</Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-96 overflow-y-auto">
                  {scrapedData.contacts.map((contact, index) => (
                    <div
                      key={index}
                      className={`p-4 border rounded-lg cursor-pointer transition-all ${
                        selectedContacts.includes(index)
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => toggleContactSelection(index)}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">
                            {contact.name || 'No name'}
                          </p>
                          <p className="text-sm text-gray-600">
                            {contact.businessName || contact.company || contact.title || 'No company'}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditContact(index);
                          }}
                        >
                          <Edit2 className="w-3 h-3" />
                        </Button>
                      </div>
                      <div className="space-y-1 text-xs text-gray-500">
                        {contact.email && (
                          <p className="truncate">📧 {contact.email}</p>
                        )}
                        {contact.phone && (
                          <p>📞 {contact.phone}</p>
                        )}
                        {contact.website && (
                          <p className="truncate">🌐 {contact.website}</p>
                        )}
                      </div>
                      {selectedContacts.includes(index) && (
                        <CheckCircle className="w-4 h-4 text-blue-600 mt-2" />
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Create Group Form */}
            <Card>
              <CardHeader>
                <CardTitle>Create Contact Group</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Group Name *
                  </label>
                  <Input
                    type="text"
                    placeholder="e.g., BNI Hawaii Chapter"
                    value={groupName}
                    onChange={(e) => setGroupName(e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Group Description
                  </label>
                  <Input
                    type="text"
                    placeholder="Optional description"
                    value={groupDescription}
                    onChange={(e) => setGroupDescription(e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Default Location
                  </label>
                  <Input
                    type="text"
                    placeholder="e.g., Hawaii"
                    value={defaultLocation}
                    onChange={(e) => setDefaultLocation(e.target.value)}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Will be used for contacts without address
                  </p>
                </div>

                <Button
                  onClick={handleCreateGroup}
                  disabled={creating || selectedContacts.length === 0}
                  className="w-full"
                >
                  {creating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating Group...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      Create Contact Group ({selectedContacts.length} contacts)
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </>
        )}

        {/* Edit Contact Modal */}
        {editingContact && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <CardHeader>
                <CardTitle>Edit Contact</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                    <Input
                      value={editingContact.name || ''}
                      onChange={(e) => setEditingContact({...editingContact, name: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Business Name</label>
                    <Input
                      value={editingContact.businessName || editingContact.company || ''}
                      onChange={(e) => setEditingContact({...editingContact, businessName: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                    <Input
                      value={editingContact.title || ''}
                      onChange={(e) => setEditingContact({...editingContact, title: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                    <Input
                      type="email"
                      value={editingContact.email || ''}
                      onChange={(e) => setEditingContact({...editingContact, email: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                    <Input
                      value={editingContact.phone || ''}
                      onChange={(e) => setEditingContact({...editingContact, phone: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Website</label>
                    <Input
                      value={editingContact.website || ''}
                      onChange={(e) => setEditingContact({...editingContact, website: e.target.value})}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                  <Input
                    value={editingContact.address || ''}
                    onChange={(e) => setEditingContact({...editingContact, address: e.target.value})}
                  />
                </div>

                <div className="flex gap-2">
                  <Button onClick={handleSaveEdit} className="flex-1">
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setEditingContact(null)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default WebScraper;
